import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import { AlmikiaPage } from "../almikia/AlmikiaPage";
import alertRules from "../alertRules";
import { AiUsageScreen } from "../aiUsage/AiUsageScreen";
import { PilotReportsScreen } from "../feedback/PilotReportsScreen";
import { GuardiasScreen } from "../guardias/GuardiasScreen";
import { InboxScreen } from "../whatsapp/InboxScreen";
import { PersonalNumbersScreen } from "../whatsapp/PersonalNumbersScreen";
import { UnclassifiedTray } from "../whatsapp/UnclassifiedTray";
import { WhatsAppSettings } from "../whatsapp/WhatsAppSettings";
import { InternalNoticesSettings } from "../internalNotices/InternalNoticesSettings";
import {
  AssignmentLog,
  LeadAssignmentConfig,
  MyAvailability,
} from "../leadAssignment";
import {
  PlanTemplateList,
  PlansToApprove,
  QuoterConfigPage,
  QuoterPanel,
} from "../quoter";
import { SalesToolLibrary } from "../salesTools";
import { CultureDocumentEditor } from "../culture/CultureDocumentEditor";
import { CulturePage } from "../culture/CulturePage";
import clients from "../clients";
import companies from "../companies";
import contacts from "../contacts";
import operations from "../operations";
import expenses, {
  expenseCategories,
  ExpenseMonthlyTotals,
  ExpenseReimbursements,
} from "../expenses";
import receivables, { payables, VoboPendingPage } from "../invoicing";
import opciones from "../opciones";
import { AdvisorToday } from "../dashboard/AdvisorToday";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import { RoleHome } from "../dashboard/RoleHome";
import deals from "../deals";
import { GoalsPage } from "../goals/GoalsPage";
import { MyProgressPage } from "../incentives/MyProgressPage";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { PublicQuotePage } from "../quoter/PublicQuotePage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import { ProfileOptionsPage } from "../profileOptions/ProfileOptionsPage";
import { PropertyTypesPage } from "../propertyTypes/PropertyTypesPage";
import { LeadSourceList } from "../leadSources/LeadSourceList";
import { ColegaList } from "../colegas/ColegaList";
import {
  ColegasListRedirect,
  CompaniesListRedirect,
  LEGACY_COLEGAS_SCREEN_PATH,
} from "./legacyRedirects";
import { CampaignReport } from "../metaLeads/CampaignReport";
import { MetaEventsScreen } from "../metaLeads/MetaEventsScreen";
import { MetaLeadsStatus } from "../metaLeads/MetaLeadsStatus";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import activityCatalogue from "../activityCatalogue";
import developers from "../developers";
import incentivePrograms from "../incentives";
import { OrgChartPage } from "../orgchart/OrgChartPage";
import projects from "../projects";
import stageConfigs from "../stageConfigs";
import type { Unit } from "../types";

const defaultStore = localStorageStore(undefined, "CRM");

/**
 * Almikia Epic E7 (TASK-089): an asesora's home is "Hoy"; every other role
 * keeps the pre-E7 desktop `Dashboard` until its own home ships.
 */
const DesktopHome = () => <RoleHome fallback={Dashboard} />;

/** Same switch as {@link DesktopHome}, for the mobile shell. */
const MobileHome = () => <RoleHome fallback={MobileDashboard} />;

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI — never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? DesktopHome}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        <Route path={PublicQuotePage.path} element={<PublicQuotePage />} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        {/* Almikia Epic EF (TASK-121): "Opciones del perfil" -- direccion-only,
            gated inside `ProfileOptionsPage` itself via `CanAccess`, reached
            from Settings on desktop and mobile; NOT added to the desktop top
            rail (ticket's own acceptance criterion). */}
        <Route
          path={ProfileOptionsPage.path}
          element={<ProfileOptionsPage />}
        />
        {/* Almikia Epic EC2d (TASK-138): direccion-only "Tipos de propiedad", gated inside
            `PropertyTypesPage`, reached from Settings (not on the top rail). */}
        <Route path={PropertyTypesPage.path} element={<PropertyTypesPage />} />
        {/* Almikia Epic E2b (TASK-022): direccion-only sources and the
            commercial-roles Colegas list, each gated inside its own page. */}
        <Route path={LeadSourceList.path} element={<LeadSourceList />} />
        {/* Almikia Epic EC2a: the Colegas tab is gone (colegas are contacts of type Colega);
            /colegas redirects to the contacts list with that type filter, the legacy screen
            stays registered, unlinked. */}
        <Route path={ColegaList.path} element={<ColegasListRedirect />} />
        <Route path={LEGACY_COLEGAS_SCREEN_PATH} element={<ColegaList />} />
        {/* Almikia Epic E9 (TASK-049): direccion-only Meta webhook status. */}
        <Route path={MetaLeadsStatus.path} element={<MetaLeadsStatus />} />
        {/* Almikia Epic EM (TASK-068): direccion / lead_manager campaign report. */}
        <Route path={CampaignReport.path} element={<CampaignReport />} />
        {/* Almikia Epic EM (TASK-069): direccion-only Conversions API events. */}
        <Route path={MetaEventsScreen.path} element={<MetaEventsScreen />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        {/* Almikia Epic E7 (TASK-089): reachable regardless of role, always
            scoped to the signed-in user's own records -- so a Líder BPO can
            later open her own day (TASK-093) from her own home. */}
        <Route path="/hoy" element={<AdvisorToday />} />
        {/* Almikia Epic E7 (TASK-092): reachable with one tap from Hoy in
            both shells, same "own records only" scoping as /hoy. */}
        <Route path={MyProgressPage.path} element={<MyProgressPage />} />
        {/* Almikia Epic E7 (TASK-091): desktop only (ASSUMPTION, like
            Clientes) -- gated inside `GoalsPage` itself via `canAccess`. */}
        <Route path={GoalsPage.path} element={<GoalsPage />} />
        {/* Almikia Epic EI (TASK-005): "Uso de IA" -- direccion-only, gated
            inside `AiUsageScreen` itself via `CanAccess`. */}
        <Route path={AiUsageScreen.path} element={<AiUsageScreen />} />
        {/* Almikia Epic PL (TASK-062): "Reportes del piloto" -- direccion-only,
            gated inside `PilotReportsScreen` itself via `CanAccess`. */}
        <Route
          path={PilotReportsScreen.path}
          element={<PilotReportsScreen />}
        />
        {/* Almikia Epic EL (TASK-008): "Asignación de leads" -- direccion-only,
            gated inside `LeadAssignmentConfig` itself via `CanAccess`. */}
        <Route
          path={LeadAssignmentConfig.path}
          element={<LeadAssignmentConfig />}
        />
        {/* Almikia Epic EL (TASK-009): "Mi disponibilidad" -- asesora and
            lider only, gated inside `MyAvailability` itself via `CanAccess`. */}
        <Route path={MyAvailability.path} element={<MyAvailability />} />
        {/* Almikia Epic EL2 (TASK-074): "Guardias y asignación", gated inside
            `GuardiasScreen` itself via `CanAccess` and a per-project check. */}
        <Route path={GuardiasScreen.path} element={<GuardiasScreen />} />
        {/* Almikia Epic EW2 (TASK-080): the WhatsApp Inbox (direccion and lead_manager) and the
            direccion-only WhatsApp settings, each gated inside its own screen via `CanAccess`. */}
        <Route path={InboxScreen.path} element={<InboxScreen />} />
        <Route path={WhatsAppSettings.path} element={<WhatsAppSettings />} />
        {/* Almikia Epic EW3 (TASK-095): 'Sin clasificar' tray and personal numbers, gated inside
            each screen for the holder of the per-person permission only. */}
        <Route path={UnclassifiedTray.path} element={<UnclassifiedTray />} />
        <Route
          path={PersonalNumbersScreen.path}
          element={<PersonalNumbersScreen />}
        />
        {/* Almikia Epic EN (TASK-091): direccion-only "Avisos internos", gated inside the screen. */}
        <Route
          path={InternalNoticesSettings.path}
          element={<InternalNoticesSettings />}
        />
        {/* Almikia Epic EL (TASK-010): "Bitácora de asignación", gated inside
            `AssignmentLog` itself via `CanAccess`. */}
        <Route path={AssignmentLog.path} element={<AssignmentLog />} />
        {/* Almikia Epic EH (TASK-013): "Biblioteca de herramientas", gated
            inside `SalesToolLibrary` itself via `CanAccess`. */}
        <Route path={SalesToolLibrary.path} element={<SalesToolLibrary />} />
        {/* Almikia Epic EQ (TASK-018): "Planes de pago", direccion only, gated
            inside `PlanTemplateList` itself via `CanAccess`. */}
        <Route path={PlanTemplateList.path} element={<PlanTemplateList />} />
        {/* Almikia Epic EQ2 (TASK-099): "Configuración del cotizador" and the
            quoter panel, direccion only, each gated inside the screen itself
            via `CanAccess` (lead_manager only holds the custom plan right). */}
        <Route path={QuoterConfigPage.path} element={<QuoterConfigPage />} />
        <Route path={QuoterPanel.path} element={<QuoterPanel />} />
        {/* Almikia batch EQ3 (TASK-115): "Planes por aprobar", gated inside
            the screen (direccion and the active approvers). */}
        <Route path={PlansToApprove.path} element={<PlansToApprove />} />
        {/* Almikia Epic E6 (TASK-040): "Pendientes de VoBo", direccion only,
            gated inside `VoboPendingPage` itself. */}
        <Route path={VoboPendingPage.path} element={<VoboPendingPage />} />
        {/* Almikia Epic EG (TASK-045): "Totales mensuales" and "Por
            reembolsar al socio", desktop only; each page gates itself via
            `useCanAccess` (the four expense roles). */}
        <Route
          path={ExpenseMonthlyTotals.path}
          element={<ExpenseMonthlyTotals />}
        />
        <Route
          path={ExpenseReimbursements.path}
          element={<ExpenseReimbursements />}
        />
        {/* Almikia Epic EO (TASK-098/TASK-099): reachable for every role,
            both shells -- the Almikia section landing, its Organigrama and
            its Nuestra cultura. */}
        <Route path={AlmikiaPage.path} element={<AlmikiaPage />} />
        <Route path={OrgChartPage.path} element={<OrgChartPage />} />
        <Route path={CulturePage.path} element={<CulturePage />} />
        {/* Almikia Epic EO (TASK-100): direccion-only, gated inside
            CultureDocumentEditor itself via CanAccess -- "Editar" on an
            existing document or "Nuevo documento". */}
        <Route
          path={CultureDocumentEditor.newPath}
          element={<CultureDocumentEditor />}
        />
        <Route
          path={CultureDocumentEditor.path}
          element={<CultureDocumentEditor />}
        />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      {/* Almikia Epic EC2a: no Empresas tab; the list route redirects to the contacts list. */}
      <Resource name="companies" {...companies} list={CompaniesListRedirect} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
      <Resource name="projects" {...projects} />
      <Resource name="developers" {...developers} />
      {/* Bare resource so references (project pickers) resolve to a readable
          name; no units UI is planned for E1 (backlog open question). */}
      <Resource
        name="units"
        recordRepresentation={(record: Unit) => record.identifier}
      />
      {/* Almikia Epic E3: desktop only (ASSUMPTION), not registered in
          MobileAdmin below. */}
      <Resource name="clients" {...clients} />
      {/* Almikia Epic E4 (TASK-027): desktop only; gated by canAccess. */}
      <Resource name="operations" {...operations} />
      <Resource name="operation_advisor_history" />
      {/* Almikia Epic E6 (TASK-040): "Cuentas por cobrar", desktop; gated by
          canAccess (direccion, contabilidad, auditoria). */}
      <Resource name="receivables" {...receivables} />
      {/* Almikia Epic E6 (TASK-041): "Cuentas por pagar" / "Mis pagos". */}
      <Resource name="payables" {...payables} />
      {/* Almikia Epic EG (TASK-044): "Gastos" and "Categorías de gastos",
          desktop only; gated by canAccess (direccion, lead_manager,
          administracion, contabilidad; categories written by direccion). */}
      <Resource name="expenses" {...expenses} />
      <Resource name="expense_categories" {...expenseCategories} />
      {/* Almikia Epic EC (TASK-030): "Opciones", gated by canAccess. */}
      <Resource name="property_options" {...opciones} />
      {/* Almikia Epic E7 (TASK-095): desktop only (ASSUMPTION, like
          Clientes/Metas) -- gated inside `IncentiveProgramList`/
          `IncentiveProgramEdit` themselves via `canAccess`. */}
      <Resource name="incentive_programs" {...incentivePrograms} />
      {/* Almikia Epic EP (TASK-105): "Etapas" -- direccion-only, gated
          inside each `stageConfigs` screen via `CanAccess`, reached from the
          desktop rail (`Header.tsx`). */}
      <Resource name="deal_stage_configs" {...stageConfigs} />
      {/* Almikia Epic EA (TASK-113): "Actividades" -- direccion-only, gated
          inside each `activityCatalogue` screen via `CanAccess`, reached
          from the desktop rail (`Header.tsx`), next to "Etapas". */}
      <Resource name="deal_activity_types" {...activityCatalogue} />
      {/* Almikia Epic ER (TASK-006): "Alertas" -- direccion-only, gated
          inside each `alertRules` screen via `CanAccess`, reached from the
          desktop rail (`Header.tsx`), next to "Etapas"/"Actividades". */}
      <Resource name="alert_rules" {...alertRules} />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileHome}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
          <Route path={PublicQuotePage.path} element={<PublicQuotePage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          {/* Almikia Epic EF (TASK-121): same "Opciones del perfil" route as
              the desktop shell, reached from `SettingsPageMobile`. */}
          <Route
            path={ProfileOptionsPage.path}
            element={<ProfileOptionsPage />}
          />
          <Route
            path={PropertyTypesPage.path}
            element={<PropertyTypesPage />}
          />
          <Route path={LeadSourceList.path} element={<LeadSourceList />} />
          <Route path={ColegaList.path} element={<ColegasListRedirect />} />
          <Route path={LEGACY_COLEGAS_SCREEN_PATH} element={<ColegaList />} />
          <Route path={MetaLeadsStatus.path} element={<MetaLeadsStatus />} />
          <Route path={CampaignReport.path} element={<CampaignReport />} />
          <Route path={MetaEventsScreen.path} element={<MetaEventsScreen />} />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          {/* Almikia Epic E7 (TASK-089): same reachable-anywhere Hoy route as
              the desktop shell. */}
          <Route path="/hoy" element={<AdvisorToday />} />
          {/* Almikia Epic E7 (TASK-092): same reachable-anywhere Mi avance
              route as the desktop shell. */}
          <Route path={MyProgressPage.path} element={<MyProgressPage />} />
          {/* Almikia Epic EI (TASK-005): same direccion-only "Uso de IA"
              route as the desktop shell, reached from `SettingsPageMobile`. */}
          <Route path={AiUsageScreen.path} element={<AiUsageScreen />} />
          {/* Almikia Epic PL (TASK-062): same direccion-only route as the
              desktop shell, reached from `SettingsPageMobile`. */}
          <Route
            path={PilotReportsScreen.path}
            element={<PilotReportsScreen />}
          />
          {/* Almikia Epic EL (TASK-009): same "Mi disponibilidad" route as the
              desktop shell, reached from `SettingsPageMobile`. */}
          <Route path={MyAvailability.path} element={<MyAvailability />} />
          {/* Almikia Epic EL2 (TASK-074): same "Guardias y asignación" route
              as the desktop shell, reached from `SettingsPageMobile`. */}
          <Route path={GuardiasScreen.path} element={<GuardiasScreen />} />
          {/* Almikia Epic EW2 (TASK-080): same Inbox and WhatsApp settings routes as desktop. */}
          <Route path={InboxScreen.path} element={<InboxScreen />} />
          <Route path={WhatsAppSettings.path} element={<WhatsAppSettings />} />
          {/* Almikia Epic EW3 (TASK-095): same tray and personal-numbers routes as desktop. */}
          <Route path={UnclassifiedTray.path} element={<UnclassifiedTray />} />
          <Route
            path={PersonalNumbersScreen.path}
            element={<PersonalNumbersScreen />}
          />
          {/* Almikia Epic EN (TASK-091): same "Avisos internos" route as desktop. */}
          <Route
            path={InternalNoticesSettings.path}
            element={<InternalNoticesSettings />}
          />
          {/* Almikia Epic EL (TASK-010): same "Bitácora de asignación" route
              as the desktop shell, reached from `SettingsPageMobile`. */}
          <Route path={AssignmentLog.path} element={<AssignmentLog />} />
          <Route path={SalesToolLibrary.path} element={<SalesToolLibrary />} />
          <Route path={PlanTemplateList.path} element={<PlanTemplateList />} />
          <Route path={QuoterConfigPage.path} element={<QuoterConfigPage />} />
          <Route path={QuoterPanel.path} element={<QuoterPanel />} />
          <Route path={PlansToApprove.path} element={<PlansToApprove />} />
          <Route path={VoboPendingPage.path} element={<VoboPendingPage />} />
          {/* Almikia Epic EO (TASK-098/TASK-099): same reachable-anywhere
              Almikia section as the desktop shell. */}
          <Route path={AlmikiaPage.path} element={<AlmikiaPage />} />
          <Route path={OrgChartPage.path} element={<OrgChartPage />} />
          <Route path={CulturePage.path} element={<CulturePage />} />
          <Route
            path={CultureDocumentEditor.newPath}
            element={<CultureDocumentEditor />}
          />
          <Route
            path={CultureDocumentEditor.path}
            element={<CultureDocumentEditor />}
          />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource
          name="companies"
          list={CompaniesListRedirect}
          show={CompanyShow}
        />
        <Resource name="tasks" list={MobileTasksList} />
        {/* Almikia Epic EP (TASK-105): "Etapas" -- reached from the mobile
            settings page (`SettingsPageMobile.tsx`), not from the bottom tab
            bar; gated the same direccion-only way as the desktop shell. */}
        <Resource name="deal_stage_configs" {...stageConfigs} />
        {/* Almikia Epic EA (TASK-113): "Actividades" -- reached from the
            mobile settings page (`SettingsPageMobile.tsx`), same
            direccion-only gate as the desktop shell. */}
        <Resource name="deal_activity_types" {...activityCatalogue} />
        {/* Almikia Epic ER (TASK-006): "Alertas" -- reached from the mobile
            settings page (`SettingsPageMobile.tsx`), same direccion-only
            gate as the desktop shell. */}
        <Resource name="alert_rules" {...alertRules} />
        {/* Almikia Epic E4 (TASK-027): "Operaciones" -- reached from the
            mobile settings page (`SettingsPageMobile.tsx`); gated by
            canAccess like the desktop shell. */}
        <Resource name="operations" {...operations} />
        <Resource name="operation_advisor_history" />
        {/* Almikia Epic E6 (TASK-040): "Cuentas por cobrar"; gated by
            canAccess like the desktop shell. */}
        <Resource name="receivables" {...receivables} />
        <Resource name="payables" {...payables} />
        {/* Almikia Epic EC (TASK-030): "Opciones" -- reached from the mobile
            settings page; gated by canAccess like the desktop shell. */}
        <Resource name="property_options" {...opciones} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
